import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useTheme } from '../contexts/ThemeContext';
import { useSubscription } from '../hooks/useSubscription';

// ── Trial Banner (shown inside app when trialing) ────────────────────
export function TrialBanner() {
  const { theme } = useTheme();
  const { subscription } = useSubscription();

  if (subscription.status !== 'trialing') return null;

  const days = subscription.trialDaysRemaining ?? 0;
  const isUrgent = days <= 2;

  return (
    <div style={{
      background: isUrgent
        ? `linear-gradient(135deg, #EF4444 0%, #DC2626 100%)`
        : theme.gradients.brand,
      padding: '10px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: '8px',
    }}>
      <p style={{ color: '#fff', margin: 0, fontSize: '14px', fontWeight: 500 }}>
        {isUrgent
          ? `⚠️ Your trial ends ${days === 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`}. Your subscription will activate automatically.`
          : `🎉 You're on a free trial — ${days} day${days === 1 ? '' : 's'} remaining. You won't be charged until your trial ends.`
        }
      </p>
    </div>
  );
}

// ── Lockout Screen (shown when subscription expired/canceled) ────────
export function LockoutScreen() {
  const { theme } = useTheme();
  const { subscription } = useSubscription();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const getMessage = () => {
    switch (subscription.status) {
      case 'canceled':
        return {
          title: 'Your subscription has been cancelled',
          body: 'Resubscribe to regain access to your files. Your files are safely stored and waiting for you.',
          icon: '📦',
        };
      case 'past_due':
        return {
          title: 'Payment failed',
          body: "We couldn't process your last payment. Please update your billing details to restore access.",
          icon: '💳',
        };
      case 'unpaid':
        return {
          title: 'Outstanding payment',
          body: 'Your account has an unpaid invoice. Please subscribe to restore access to your files.',
          icon: '⚠️',
        };
      case 'none':
        return {
          title: 'Start your free trial',
          body: 'Get 7 days free, then just $4.99 AUD billed annually. Cancel anytime.',
          icon: '🚀',
        };
      default:
        return {
          title: 'Subscription required',
          body: 'A subscription is required to access MyPortal. Get started with a 7-day free trial.',
          icon: '🔒',
        };
    }
  };

  const { title, body, icon } = getMessage();

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.colors.bgPrimary,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
    }}>
      {/* Glow */}
      <div style={{
        position: 'absolute',
        top: '20%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '600px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(157,78,221,0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        background: theme.colors.bgSecondary,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: '20px',
        padding: '48px',
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
        position: 'relative',
        zIndex: 1,
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
      }}>

        {/* Brand Logo */}
        <div style={{ marginBottom: '16px' }}>
          <img
            src="/logo.svg"
            alt="Skydrive Logo"
            style={{ height: '60px', width: 'auto', maxWidth: '200px', objectFit: 'contain' }}
          />
        </div>

        {/* Icon */}
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>{icon}</div>

        {/* Title */}
        <h1 style={{
          fontSize: '26px',
          fontWeight: 700,
          color: theme.colors.textPrimary,
          marginBottom: '12px',
          lineHeight: 1.3,
        }}>
          {title}
        </h1>

        {/* Body */}
        <p style={{
          fontSize: '16px',
          color: theme.colors.textSecondary,
          marginBottom: '32px',
          lineHeight: 1.6,
        }}>
          {body}
        </p>

        {/* Pricing summary */}
        <div style={{
          background: theme.colors.bgTertiary,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '28px',
        }}>
          <div style={{
            fontSize: '36px',
            fontWeight: 700,
            background: theme.gradients.brand,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '4px',
          }}>
            $4.99 <span style={{ fontSize: '18px' }}>AUD/mo</span>
          </div>
          <p style={{ color: theme.colors.textSecondary, fontSize: '13px', margin: 0 }}>
            Billed annually · 7-day free trial · Cancel anytime
          </p>
        </div>

        {/* Features */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          marginBottom: '32px',
          textAlign: 'left',
        }}>
          {[
            '50 GB secure cloud storage',
            'Unlimited file uploads',
            'Secure 24-hour share links',
            '30-day trash recovery',
            'All future features included',
          ].map((f) => (
            <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ color: theme.colors.brand, fontSize: '16px' }}>✓</span>
              <span style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>{f}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <button
          onClick={() => navigate('/pricing')}
          style={{
            width: '100%',
            padding: '16px',
            background: theme.gradients.brand,
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: '12px',
            transition: 'transform 0.2s, box-shadow 0.2s',
            boxShadow: '0 4px 16px rgba(157,78,221,0.4)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(157,78,221,0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(157,78,221,0.4)';
          }}
        >
          {subscription.status === 'none' ? 'Start Free Trial →' : 'Resubscribe →'}
        </button>

        {/* Sign out link */}
        <button
          onClick={() => instance.logoutRedirect({ account: accounts[0] ?? null, postLogoutRedirectUri: '/' })}
          style={{
            background: 'transparent',
            border: 'none',
            color: theme.colors.textTertiary,
            fontSize: '13px',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: '4px',
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
